import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTenant, useTenantBranding, useTenantFeatures } from '@/contexts/TenantContext';
import { Button } from '@/components/Button';
import { 
  Building2, 
  MapPin, 
  Briefcase, 
  DollarSign, 
  Clock,
  Users,
  Heart,
  TrendingUp,
  Search
} from 'lucide-react';

interface Job {
  id: string;
  title: string;
  department: string;
  location: string;
  type: 'full-time' | 'part-time' | 'contract';
  salaryRange?: string;
  postedDate: string;
  description: string;
}

export default function CareersPage() {
  const { tenant, isLoading, isTenantDomain } = useTenant();
  const { orgName, logoUrl } = useTenantBranding();
  const { hasCareerPage } = useTenantFeatures();
  const [searchQuery, setSearchQuery] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);

  useEffect(() => {
    // TODO: Fetch jobs from API based on tenant
    // Mock data for now
    setJobs([
      {
        id: '1',
        title: 'Senior Software Engineer',
        department: 'Engineering',
        location: 'San Francisco, CA',
        type: 'full-time',
        salaryRange: '$120k - $180k',
        postedDate: '2024-11-01',
        description: 'We are looking for an experienced software engineer...',
      },
      {
        id: '2',
        title: 'Product Manager',
        department: 'Product',
        location: 'Remote',
        type: 'full-time',
        salaryRange: '$100k - $150k',
        postedDate: '2024-10-28',
        description: 'Join our product team to drive innovation...',
      },
      {
        id: '3',
        title: 'UX Designer',
        department: 'Design',
        location: 'New York, NY',
        type: 'full-time',
        salaryRange: '$90k - $130k',
        postedDate: '2024-10-25',
        description: 'Create beautiful and intuitive user experiences...',
      },
    ]);
  }, [tenant]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading careers page...</p>
        </div>
      </div>
    );
  }

  if (!isTenantDomain || !tenant || !hasCareerPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Careers Page Not Available
          </h1>
          <p className="text-gray-600 mb-6">
            This organization does not have a public careers page enabled.
          </p>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const filteredJobs = jobs.filter(job =>
    job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {logoUrl ? (
                <img src={logoUrl} alt={orgName} className="h-10 w-auto" />
              ) : (
                <Building2 className="h-10 w-10 text-primary-600" />
              )}
              <div>
                <h1 className="text-xl font-bold text-gray-900">{orgName}</h1>
                <p className="text-sm text-gray-600">Careers</p>
              </div>
            </div>
            <Link to="/login">
              <Button variant="outline">Sign In</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-4xl font-bold mb-4">Join Our Team</h2>
          <p className="text-xl text-blue-100 max-w-2xl">
            We're building the future of recruitment. Be part of something great.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 mt-12 max-w-2xl">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Users className="h-6 w-6 text-blue-200" />
                <span className="text-3xl font-bold">50+</span>
              </div>
              <p className="text-blue-100">Team Members</p>
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <TrendingUp className="h-6 w-6 text-blue-200" />
                <span className="text-3xl font-bold">5x</span>
              </div>
              <p className="text-blue-100">Growth Rate</p>
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Heart className="h-6 w-6 text-blue-200" />
                <span className="text-3xl font-bold">4.8</span>
              </div>
              <p className="text-blue-100">Glassdoor Rating</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search positions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Job Listings */}
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            Open Positions ({filteredJobs.length})
          </h3>

          {filteredJobs.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {searchQuery ? 'No positions match your search.' : 'No open positions at this time.'}
              </p>
            </div>
          ) : (
            filteredJobs.map((job) => (
              <div
                key={job.id}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">
                      {job.title}
                    </h4>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                      <div className="flex items-center">
                        <Building2 className="h-4 w-4 mr-1" />
                        {job.department}
                      </div>
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        {job.location}
                      </div>
                      <div className="flex items-center">
                        <Briefcase className="h-4 w-4 mr-1" />
                        {job.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                      {job.salaryRange && (
                        <div className="flex items-center">
                          <DollarSign className="h-4 w-4 mr-1" />
                          {job.salaryRange}
                        </div>
                      )}
                      <div className="flex items-center text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        Posted {new Date(job.postedDate).toLocaleDateString()}
                      </div>
                    </div>

                    <p className="text-gray-700 mb-4 line-clamp-2">
                      {job.description}
                    </p>
                  </div>

                  <Link to={`/jobs/${job.id}/apply`}>
                    <Button className="ml-4">
                      Apply Now
                    </Button>
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-gray-600">
              <Building2 className="h-5 w-5" />
              <span className="text-sm">© 2024 {orgName}. All rights reserved.</span>
            </div>
            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <Link to="/privacy" className="hover:text-gray-900">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-gray-900">Terms of Service</Link>
              <a 
                href={`https://${tenant.subdomain}.epeople.work`}
                className="hover:text-gray-900"
              >
                Company Portal
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
